import React, { useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LayoutGrid, Inbox, CreditCard, Percent, Users, ScrollText, LogOut, Menu, ShieldCheck, Coins } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { adminApi } from "@/lib/api";
import { Logo } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/admin", label: "Overview", Icon: LayoutGrid, id: "overview", end: true },
  { to: "/admin/deposits", label: "Deposit requests", Icon: Inbox, id: "deposits", badge: true },
  { to: "/admin/sell-orders", label: "Sell orders", Icon: Coins, id: "sell-orders" },
  { to: "/admin/payment-methods", label: "Payment methods", Icon: CreditCard, id: "payment-methods" },
  { to: "/admin/bonuses", label: "Bonuses & rates", Icon: Percent, id: "bonuses" },
  { to: "/admin/users", label: "Users", Icon: Users, id: "users" },
  { to: "/admin/audit-logs", label: "Audit log", Icon: ScrollText, id: "audit-logs" },
];

function SideNav({ open, onNavigate }) {
  const { data } = useQuery({
    queryKey: ["admin-stats-badge"],
    queryFn: () => adminApi.get("/admin/stats").then((r) => r.data),
    refetchInterval: 20000,
    enabled: open,
  });
  return (
    <div className="flex h-full flex-col bg-slate-950 text-slate-300">
      <div className="px-5 py-5">
        <Logo dark />
        <p className="mt-2 inline-flex items-center gap-1 rounded-md bg-slate-800 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-[hsl(var(--accent))]">
          <ShieldCheck className="h-3 w-3" /> Admin
        </p>
      </div>
      <nav className="flex-1 space-y-1 px-3">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={onNavigate}
            data-testid={`admin-nav-${n.id}-link`}
            className={({ isActive }) =>
              cn("flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors", isActive ? "bg-teal-700 text-white" : "hover:bg-slate-800 hover:text-white")
            }
          >
            <n.Icon className="h-4 w-4" />
            <span className="flex-1">{n.label}</span>
            {n.badge && data?.open_requests > 0 && (
              <span data-testid="admin-nav-pending-badge" className="rounded-full bg-[hsl(var(--accent))] px-2 py-0.5 text-[11px] font-semibold text-slate-900">{data.open_requests}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="px-5 py-4 text-[11px] text-slate-500">All actions are recorded in the audit log.</div>
    </div>
  );
}

export default function AdminShell() {
  const { admin, ready, adminLogout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const nav = useNavigate();
  if (!ready) return <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 lg:block">
        <SideNav open />
      </aside>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-64 border-0 p-0">
          <SideNav open={mobileOpen} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b bg-white px-4 sm:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Open menu" data-testid="admin-mobile-menu-button">
            <Menu className="h-5 w-5" />
          </Button>
          <p className="text-sm font-medium text-muted-foreground">Payment operations</p>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden text-sm sm:inline" data-testid="admin-current-email">{admin.email}</span>
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="admin-logout-button" onClick={() => { adminLogout(); nav("/admin/login"); }}>
              <LogOut className="h-4 w-4" /> Sign out
            </Button>
          </div>
        </header>
        <main className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
